import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Alert, Button, Snackbar } from '@mui/material';
import React, { useRef, useState } from 'react';
import Company from 'services/api/Company';
import { I18n } from 'utils/i18n';

const FtpUploadButton = ({ companyId, disabled = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const openFileDialog = () => {
    if (!companyId) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async event => {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await Company.forceFtpUpload(companyId, file);
      const { status, rowErrors = [], error: pipelineError } = result || {};
      const hasErrors = status !== 'success';
      const severity = hasErrors ? 'warning' : 'success';
      const errorsMessage =
        rowErrors.length > 0
          ? I18n.t('CompanyDrawer.FTP file processed with %{count} error(s).', {
              count: rowErrors.length,
            })
          : null;
      const baseMessage =
        status === 'success'
          ? I18n.t('CompanyDrawer.FTP file processed successfully.')
          : pipelineError ||
            errorsMessage ||
            I18n.t('CompanyDrawer.FTP file processed with errors.');

      setSnackbar({
        open: true,
        severity,
        message: baseMessage,
      });
    } catch (error) {
      const message =
        error?.data?.error?.message ||
        error?.data?.message ||
        error?.message ||
        I18n.t('CompanyDrawer.FTP upload failed.');
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setIsLoading(false);
      resetInput();
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CloudUploadIcon />}
        onClick={openFileDialog}
        disabled={disabled || isLoading || !companyId}
        sx={{ minWidth: 180 }}
      >
        {isLoading
          ? I18n.t('CompanyDrawer.Uploading FTP file...')
          : I18n.t('CompanyDrawer.Upload FTP file')}
      </Button>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FtpUploadButton;
